import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  linkedSignal,
  model,
  output,
  signal,
  untracked,
  viewChildren,
  ViewEncapsulation,
} from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { firstValueFrom, type Observable } from 'rxjs';
import { BasicStoreService } from 'src/app/services/BasicStore/basic-store.service';
import { APIService } from 'src/app/services/APIService/api.service';
import dayjs from 'dayjs';
import { ToastService } from 'src/app/services/toast.service';
import { BackendLocaleDatePipe } from 'src/app/pipes/get-locale-date.pipe';
import { MrTranslatePipe } from 'src/app/pipes/mr-translate.pipe';
import { AuthService } from 'src/app/services/Auth/auth.service';
import { OverlayService } from 'src/app/services/Overlay/overlay.service';
import { DotDotDotPipe } from 'src/app/pipes/dot-dot-dot.pipe';
import { HideIDColumnsPipe } from 'src/app/pipes/hide-idcolumns.pipe';
import { TablePrettyPrintPipe } from 'src/app/pipes/tablePrettyPrint.pipe';
import { CommonModule } from '@angular/common';
import { ClarityModule, type ClrWizard } from '@clr/angular';
import {
  KostenerfassungComponent,
  type KostenSummary,
} from '../../_components/_overlays/auftrag-detail/kostenerfassung/kostenerfassung.component';
import { FindInArrayPipe } from 'src/app/pipes/array-methods.pipe';
import { rxResource, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CustomValidators } from 'src/app/shared/classes/custom-validators';
import { FormGroupOfType } from 'src/app/types/shared';
import type { Row, Table } from 'src/app/models/ui/table';
import { AuftragNewMangel } from 'src/app/types/api';
import { ColumnFilterComponent } from '../../_components/filter/column-filter/column-filter.component';
import {
  FILTER_TABLE_STORE,
  FILTER_TABLE_STORE_II,
  provideFilterStore,
} from 'src/app/shared/stores/filter-table.store';
import { unionBySort } from 'src/app/misc/functions';
import { ConfirmService } from 'src/app/services/confirm.service';
import { AllSelectedBtnComponent } from '../../_components/_shared/all-selected-btn/all-selected-btn.component';
import {
  type BaseItem,
  BaseItemsWrapperComponent,
} from '../../_components/_modals/baseitems-wrapper/baseitems-wrapper.component';
import { IconDgCellComponent } from '../../_components/_shared/datagrid-cell/icon-dg-cell.component';
import { DatalistInputComponent } from '../../_components/_shared/datalist-input/datalist-input.component';

@Component({
  selector: 'app-newauftragmodal',
  templateUrl: './newauftragmodal.component.html',
  styleUrls: ['./newauftragmodal.component.scss'],
  imports: [
    CommonModule,
    ClarityModule,
    FormsModule,
    ReactiveFormsModule,
    KostenerfassungComponent,
    BaseItemsWrapperComponent,
    ColumnFilterComponent,
    AllSelectedBtnComponent,
    IconDgCellComponent,
    DatalistInputComponent,
    TablePrettyPrintPipe,
    HideIDColumnsPipe,
    DotDotDotPipe,
    MrTranslatePipe,
    BackendLocaleDatePipe,
    FindInArrayPipe,
  ],
  providers: [provideFilterStore({ secondStore: true })],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class NewauftragmodalComponent {
  private readonly apiService = inject(APIService);
  private readonly basicStore = inject(BasicStoreService);
  private readonly toastr = inject(ToastService);
  private readonly overlay = inject(OverlayService);
  private readonly authService = inject(AuthService);
  private readonly mrTranslate = inject(MrTranslatePipe);
  private readonly confirmService = inject(ConfirmService);
  protected readonly localeDate = inject(BackendLocaleDatePipe);
  protected readonly objekteFilterStore = inject(FILTER_TABLE_STORE);
  protected readonly maengelFilterStore = inject(FILTER_TABLE_STORE_II);

  public readonly reload = output<void>();
  protected readonly isModalOpen = model<boolean>();

  readonly objekteColumnFilters = viewChildren<ColumnFilterComponent>('objekte');
  readonly maengelColumnFilters = viewChildren<ColumnFilterComponent>('maengel');

  protected readonly isObjectSelect = signal(true);
  protected readonly createNewMaengel = linkedSignal<boolean>(() => this.isObjectSelect() && false);
  protected readonly isImportedInfor = signal(false);
  protected readonly showInterfaceDetail = signal(false);
  protected readonly isImportedInforLoad = signal(false);
  protected readonly isBaseItemModalOpen = signal(false);
  protected readonly importedAuftragNr = signal('');

  protected readonly neuMaengel = signal<AuftragNewMangel[]>([]);
  protected readonly selectedMaengel = signal<Row[]>([]);
  protected readonly savedSelectedMaengel = signal<Row[]>([]);

  protected readonly selectedObjekte = signal<Row[]>([]);
  protected readonly savedSelectedObjekte = signal<Row[]>([]);

  private readonly selectedOSTAMMIDs = computed(() => this.savedSelectedObjekte().map(obj => +obj.OSTAMMID));

  private Imp_INFOR_ActivityCode = '';
  private readonly optInterface = +this.authService.decrypt(localStorage.getItem('optInterface'));
  protected readonly isHxGN_Interface = this.optInterface === 3;
  protected readonly trial: boolean;

  protected readonly isLoading = computed(
    () =>
      this.werteResource.isLoading() ||
      this.stoerResource.isLoading() ||
      this.firmenResource.isLoading() ||
      this.gruppenResource.isLoading()
  );

  protected readonly isSentLoading = signal(false);

  private readonly werteResource = rxResource({
    params: () => this.isModalOpen() || undefined,
    stream: () => this.apiService.getNeuAuftraegWerte(),
  });
  protected readonly titel = computed(() => this.werteResource.value()?.titel ?? []);
  protected readonly kostenart = computed(() => this.werteResource.value()?.kostenart ?? []);
  protected readonly auftragsart = computed(() =>
    this.isHxGN_Interface ? ['BR  = korrektiv', 'MPM = präventiv'] : (this.werteResource.value()?.auftragart ?? [])
  );

  private readonly stoerResource = rxResource({
    params: () => (this.isModalOpen() && this.isHxGN_Interface) || undefined,
    stream: () => this.apiService.getAuftragInforStoerungInhalt(),
  });
  protected readonly stoerursache = linkedSignal(() => this.stoerResource.value()?.stoerursache ?? []);
  protected readonly stoergrund = computed(() => this.stoerResource.value()?.stoergrund ?? []);
  protected readonly stoererfasser = computed(() => this.stoerResource.value()?.stoererfasser ?? []);

  private readonly firmenResource = rxResource({
    params: () => this.isModalOpen() || undefined,
    stream: () => this.apiService.getFirmen(),
  });
  protected readonly firmen = computed(
    () =>
      this.firmenResource.value()?.rows.map(firma => ({
        ID: firma.ID,
        mFirma: `${firma.Firma}, ${firma.Abteilung}, ${firma.Ort}`,
      })) ?? []
  );

  private readonly gruppenResource = rxResource({
    params: () => this.isModalOpen() || undefined,
    stream: () => this.apiService.getAuftragsGroups(),
  });
  protected readonly gruppen = this.gruppenResource.value;

  private readonly mehResource = rxResource({
    params: () => this.createNewMaengel() || undefined,
    stream: () => this.apiService.getNewMangelValues(1, { category: 'mengeneinheit', parameter: [] }),
  });
  protected readonly meh = this.mehResource.value;

  private readonly objekteResource = rxResource({
    params: () => this.isObjectSelect() || undefined,
    stream: () => this.basicStore.getComponentStoreAsync('anlagen'),
  });
  protected readonly objekte = linkedSignal(() => {
    const data = this.objekteResource.value();
    if (!data) return { rows: [], columns: [] };
    const table: Table = JSON.parse(data).anlagen as Table;
    table.rows = table.rows.filter(row => +row.rechte == 2);
    return table;
  });

  private readonly maengelResource = rxResource({
    params: () => this.isModalOpen() || undefined,
    stream: () => this.basicStore.getComponentStoreAsync('maengel'),
  });
  private readonly allMaengel = computed(() => {
    const data = this.maengelResource.value();
    if (!data) return { rows: [], columns: [] };
    const parsed = JSON.parse(data);
    const columns = (parsed.maengel.all as Table).columns;
    let rows = [
      ...(parsed.maengel.all as Table).rows,
      ...(parsed.maengel.wartung as Table).rows,
      ...(parsed.maengel.stoerung as Table).rows,
    ];
    rows = rows.filter(row => !row.auftrag && !row.erledigt && +row.rechte == 2);
    return { columns, rows } as Table;
  });

  protected readonly maengel = linkedSignal(() => {
    if (this.isObjectSelect() && this.selectedOSTAMMIDs().length)
      return {
        ...this.allMaengel(),
        rows: this.allMaengel().rows.filter(mngl => this.selectedOSTAMMIDs().includes(+mngl.ostammid)),
      };
    else return this.allMaengel();
  });

  protected readonly auftragGroup = new FormGroup({
    titel: new FormControl('', Validators.required),
    startdate: new FormControl('', Validators.required),
    enddate: new FormControl('', Validators.required),
    auftragnehmer: new FormControl<number>(null, Validators.required),
    auftragsgruppe: new FormControl(1, Validators.required),
    auftragsart: new FormControl(''), //* dynamisch Required
    auftragsnr: new FormControl(''),
    kostenart: new FormControl(''), //* dynamisch Required
    bemerkung: new FormControl(''),
    stoerbeginn: new FormControl(''),
    stoerbeginnzeit: new FormControl(''),
    stoerende: new FormControl(''),
    stoerendezeit: new FormControl(''),
    stoergrund: new FormControl(''), //* dynamisch Required
    stoerursache: new FormControl(''), //* dynamisch Required
    stoererfasser: new FormControl(''),
  });

  protected readonly newMangelGroup = new FormGroupOfType<AuftragNewMangel>({
    titel: new FormControl('', Validators.required),
    beschreibung: new FormControl(''),
    umfang: new FormControl(''),
    mengeneinheit: new FormControl(''),
    bemerkung: new FormControl(''),
  });

  protected readonly kostenTable = signal<KostenSummary>({
    rows: {
      personal: [],
      material: [],
      maschine: [],
      fremdleistung: [],
    },
    columns: null,
    summary: {
      kosten: '0.00',
      plankosten: '0.00',
    },
  });

  constructor() {
    effect(() => {
      const selected = this.selectedObjekte();
      if (selected.length) this.savedSelectedObjekte.update(saved => unionBySort(selected, saved, 'OSTAMMID'));
    });
    effect(() => {
      const selected = this.selectedMaengel();
      if (selected.length) this.savedSelectedMaengel.update(saved => unionBySort(selected, saved, 'omazsid'));
    });

    const token = this.authService.getToken();
    this.trial = token.trial;

    const laenge = token.auftragslaenge || +this.authService.decrypt(localStorage.getItem('auftraglaenge')) || 3;
    this.auftragGroup.get('startdate').setValue(BackendLocaleDatePipe.today);
    this.auftragGroup
      .get('enddate')
      .setValue(dayjs().startOf('day').add(laenge, 'days').format(BackendLocaleDatePipe.format));

    if (this.isHxGN_Interface) {
      this.auftragGroup.get('auftragsart').addValidators(Validators.required);
      this.auftragGroup.get('kostenart').addValidators(Validators.required);

      this.auftragGroup
        .get('auftragsart')
        .valueChanges.pipe(takeUntilDestroyed())
        .subscribe(art => {
          this.showInterfaceDetail.set(art == 'BR  = korrektiv');

          const stoerUrsacheCtrl = this.auftragGroup.get('stoerursache');
          const stoerGrundCtrl = this.auftragGroup.get('stoergrund');

          if (this.showInterfaceDetail()) {
            stoerUrsacheCtrl.addValidators([
              Validators.required,
              CustomValidators.InTableValidator(this.stoerursache()),
            ]);
            stoerGrundCtrl.addValidators(Validators.required);
          } else {
            stoerUrsacheCtrl.clearValidators();
            stoerGrundCtrl.clearValidators();
            stoerUrsacheCtrl.updateValueAndValidity();
            stoerGrundCtrl.updateValueAndValidity();
          }
        });
    }

    this.auftragGroup.addValidators([
      CustomValidators.StartEndDatesValidator(),
      CustomValidators.StartEndDatesValidator({ start: 'stoerbeginn', end: 'stoerende' }),
    ]);

    effect(() => {
      const werte = this.stoerursache();
      untracked(() => {
        const stoerUrsacheCtrl = this.auftragGroup.get('stoerursache');
        if (stoerUrsacheCtrl.hasValidator(Validators.required)) {
          stoerUrsacheCtrl.setValidators([Validators.required, CustomValidators.InTableValidator(werte)]);
          stoerUrsacheCtrl.updateValueAndValidity();
        }
      });
    });

    this.objekteFilterStore.registerTable(this.objekte);
    this.objekteFilterStore.registerFilters(this.objekteColumnFilters);
    this.maengelFilterStore.registerTable(this.maengel);
    this.maengelFilterStore.registerFilters(this.maengelColumnFilters);
  }

  protected deleteFromSaved(mode: 'Objekte' | 'Maengel', index: number) {
    const savedSelected = `savedSelected${mode}` as const;
    const selected = `selected${mode}` as const;

    const row = this[savedSelected]()[index];
    this[selected].update(selected => {
      const index = selected.indexOf(row);
      if (index != -1) selected.splice(index, 1);
      return [...selected];
    });
    this[savedSelected].update(saved => (saved.splice(index, 1), [...saved]));
  }

  protected toggledSelectObjekt(wizard: ClrWizard) {
    setTimeout(() => wizard.navService.setLastEnabledPageCurrent());
  }

  protected deleteNewMangel(row: AuftragNewMangel) {
    this.neuMaengel.update(maengel => (maengel.splice(maengel.indexOf(row), 1), [...maengel]));
  }

  protected createMangel() {
    const neuMangel = this.newMangelGroup.getRawValue();
    this.neuMaengel.update(maengel => (maengel.push(neuMangel), [...maengel]));
    this.newMangelGroup.reset();
  }

  protected async onSubmit() {
    if (this.auftragGroup.invalid || this.isSentLoading()) return;
    this.isSentLoading.set(true);

    try {
      const objMangel: { [ostammid: number]: number[] } | { [ostammid: number]: AuftragNewMangel[] } = {};
      if (this.createNewMaengel()) {
        for (const OSTAMMID of this.selectedOSTAMMIDs()) {
          objMangel[OSTAMMID] = this.neuMaengel();
        }
      } else {
        for (const mngl of this.savedSelectedMaengel()) {
          objMangel[+mngl.ostammid] ??= [];
          objMangel[+mngl.ostammid].push(+mngl.omazsid as any);
        }
      }

      if (this.optInterface == 3) {
        let allhasKundenid = true;
        for (const row of this.savedSelectedObjekte()) {
          if (row['KundenID'] == null) {
            allhasKundenid = false;
          }
        }
        if (!allhasKundenid) {
          const ok = await this.confirmService.warning(
            'Nicht alle Objekte haben einen EQUIPMENTCODE für HxGN EAM, möchten Sie trotzdem fortsetzten?'
          );
          if (!ok) {
            return;
          }
        }
      }

      let sendObj = {
        ...this.auftragGroup.getRawValue(),
        omazsids: objMangel,
        language: localStorage.getItem('language'),
        optInterface: this.optInterface,
        ...(this.isHxGN_Interface ?
          {
            jobnum: this.importedAuftragNr(),
            plankosten: this.kostenTable().rows,
            importstatus: this.isImportedInfor() ? 'SB -> ONGO' : '',
            imporactivitycode: this.Imp_INFOR_ActivityCode,
          }
        : {}),
      };
      if (sendObj.stoerbeginn) {
        sendObj.stoerbeginn = this.localeDate.addTimeToDate(sendObj.stoerbeginn, sendObj.stoerbeginnzeit);
      }
      if (sendObj.stoerende) {
        sendObj.stoerende = this.localeDate.addTimeToDate(sendObj.stoerende, sendObj.stoerendezeit);
      }

      const data = await firstValueFrom(this.apiService.sendAuftrag(sendObj));

      if (data) {
        this.reload.emit();
        this.toastr.success(this.mrTranslate.transform('Daten gespeichert'));

        this.overlay.setOverlay({
          overlay: 'viewAuftrag',
          data,
          auftragsnr: sendObj.auftragsnr,
        });
        this.isModalOpen.set(false);
      }
    } catch (_) {
      console.log(_);
    } finally {
      this.isSentLoading.set(false);
    }
  }

  protected async importHxGNAuftrag() {
    this.isImportedInforLoad.set(true);
    try {
      const { success, error, ...vals } = await firstValueFrom(
        this.apiService.importHxGNAuftrag(this.importedAuftragNr())
      );
      if (!success) {
        this.toastr.error(this.mrTranslate.transform(error));
        return;
      }

      if (!vals.OSTAMMID) {
        this.toastr.warning(this.mrTranslate.transform('Es konnte keinen Objekt gefunden werden'));
        return;
      }

      this.isImportedInfor.set(true);

      this.Imp_INFOR_ActivityCode = vals.ACTIVITYCODE;

      this.auftragGroup.get('auftragsart').setValue('BR  = korrektiv');
      this.auftragGroup.get('auftragsart').disable();
      this.auftragGroup.get('titel').setValue(vals.TITEL);
      this.auftragGroup.get('startdate').setValue(vals.REPORTEDDATE);

      if (vals.UDFDATE03 != '1900-01-01T00:00:00') {
        this.auftragGroup.get('stoerbeginn').setValue(vals.UDFDATE03);
        this.auftragGroup.get('stoerbeginnzeit').setValue(vals.UDFDATE03_TIME);
      }

      this.isObjectSelect.set(true);
      const s = this.objekte().rows.find(t => t.OSTAMMID.toString() === vals.OSTAMMID);
      this.savedSelectedObjekte.set([]);
      this.selectedObjekte.set([s]);
      this.objekte.update(({ columns }) => ({ columns, rows: [s] }));
      this.objekteFilterStore.deleteAllFilters();
      setTimeout(() => this.createNewMaengel.set(true), 100);
    } catch (_) {
      console.log(_);
    } finally {
      this.isImportedInforLoad.set(false);
    }
  }
}

export interface AuftragImportInfor {
  OSTAMMID: string;
  UDFDATE03: string;
  UDFCHAR14: string;
  STATUSCODE: string;
  ACTIVITYCODE: string;
  REPORTEDDATE: string;
  TARGETDATE: string;
  TITEL: string;
  UDFDATE03_TIME: string;
}
